"use strict";

/* Check If Image Exists On Server */
const imageExists = (src) =>
  fetch(src, { method: "HEAD" }).then((res) => {
    if (res.ok) {
      return res.url;
    } else {
      return false;
    }
  });

/* Determine Which Image Type To Display */
const nextGenImages = (src, fallbackExtension, resolve) => {
  // Check if browser supports avif
  const avifSupported = new Promise((resolve, reject) => {
    const image = new Image();
    image.src =
      "data:image/avif;base64,AAAAIGZ0eXBhdmlmAAAAAGF2aWZtaWYxbWlhZk1BMUIAAADybWV0YQAAAAAAAAAoaGRscgAAAAAAAAAAcGljdAAAAAAAAAAAAAAAAGxpYmF2aWYAAAAADnBpdG0AAAAAAAEAAAAeaWxvYwAAAABEAAABAAEAAAABAAABGgAAABoAAAAoaWluZgAAAAAAAQAAABppbmZlAgAAAAABAABhdjAxQ29sb3IAAAAAamlwcnAAAABLaXBjbwAAABRpc3BlAAAAAAAAAAEAAAABAAAAEHBpeGkAAAAAAwgICAAAAAxhdjFDgQAMAAAAABNjb2xybmNseAACAAIABoAAAAAXaXBtYQAAAAAAAAABAAEEAQKDBAAAACJtZGF0EgAKCBgABogQEDQgMgwYQAAAUAAAALASmkg=";
    image.onerror = () => reject("avif not supported");
    image.onload = () => resolve("avif");
  });

  // Check if browser supports webp
  const webpSupported = new Promise((resolve, reject) => {
    const image = new Image();
    image.src =
      "data:image/webp;base64,UklGRiQAAABXRUJQVlA4IBgAAAAwAQCdASoBAAEAAgA0JaQAA3AA/vuUAAA=";
    image.onerror = () => reject("webp not supported");
    image.onload = () => resolve("webp");
  });

  const avif = src.split(".").slice(0, -1).join(".").concat(".avif"); // Chrome, Firefox, Android
  const webp = src.split(".").slice(0, -1).join(".").concat(".webp"); // Safari / iOS >= 14, Edge
  const fallback = src
    .split(".")
    .slice(0, -1)
    .join(".")
    .concat(`.${fallbackExtension}`); // Safari / iOS <= 13

  const avifImageExists = () => {
    imageExists(avif).then((avifUrl) =>
      avifUrl ? resolve(avifUrl) : webpImageExists()
    );
  };

  // Check if webp image file exists on server, otherwise load fallback
  const webpImageExists = () => {
    imageExists(webp).then((webpUrl) =>
      webpUrl
        ? resolve(webpUrl)
        : imageExists(fallback).then(
            (fallbackUrl) => fallbackUrl && resolve(fallbackUrl)
          )
    );
  };

  // Load image dependant on browser/device support
  avifSupported
    .then(() => avifImageExists())
    .catch(() => {
      webpSupported
        .then(() => webpImageExists())
        .catch(() =>
          imageExists(fallback).then(
            (fallbackUrl) => fallbackUrl && resolve(fallbackUrl)
          )
        );
    });
};

export const lazyload = () => {
  const config = {
    rootMargin: "0px 0px 50px 0px",
    threshold: 0,
  };

  // Handle the image src
  const imageSrc = (el) =>
    new Promise((resolve, reject) => {
      if (el.tagName === "PICTURE") return reject();
      const src = el.getAttribute("data-src");
      if (
        src.includes(".svg") ||
        src.includes(".gif") ||
        !el.classList.contains("nextgen-img")
      ) {
        resolve(src);
      } else {
        const extension = src.split(".")[1];
        nextGenImages(src, extension, resolve);
      }
    });

  // Handle element image is on
  const imageElement = (el, src) => {
    const imgTag = el.tagName === "IMG";
    const iframeTag = el.tagName === "IFRAME";
    if (imgTag || iframeTag) {
      if (el.src !== src) el.src = src;
    } else {
      if (el.style.backgroundImage !== 'url("' + src + '")') {
        el.style.backgroundImage = 'url("' + src + '")';
      }
    }
  };

  // Handle picture element
  const pictureElement = (el) => {
    const sources = el.querySelectorAll("source");
    const img = el.querySelector("img");
    sources.forEach((source) => {
      if (source.srcset !== source.getAttribute("data-srcset")) {
        source.srcset = source.getAttribute("data-srcset");
      }
    });
    if (img) {
      if (img.src !== img.getAttribute("data-src"))
        img.src = img.getAttribute("data-src");
    }
  };

  // Set images
  const setImages = (el) => {
    imageSrc(el)
      .then((image) => {
        const mediaQuery = el.getAttribute("data-media") ?? "screen";
        if (window.matchMedia(mediaQuery).matches) {
          imageElement(el, image);
          el.classList.add("lazyloaded");
        }
      })
      .catch(() => {
        pictureElement(el);
        el.classList.add("lazyloaded");
      });
  };

  // Reveal intersecting images
  const handleImageObserver = (observerEntry, observer) => {
    if (observerEntry.isIntersecting) {
      const notPicture = observerEntry.target.tagName !== "PICTURE";
      observer.unobserve(observerEntry.target);
      setImages(observerEntry.target);
      window.addEventListener(
        "resize",
        () => notPicture && setImages(observerEntry.target)
      );
      window.addEventListener(
        "orientationchange",
        () => notPicture && setImages(observerEntry.target)
      );
    }
  };

  // Intersection observer
  const lazyloadInit = () => {
    const imageObserver = new IntersectionObserver((entries, self) => {
      entries.forEach((entry) => handleImageObserver(entry, self));
    }, config);
    document
      .querySelectorAll(".js-lazyload")
      .forEach((lazyloadImage) => imageObserver.observe(lazyloadImage));
  };
  lazyloadInit();
};
